import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InteractiveChart = () => {
  const [selectedCrypto, setSelectedCrypto] = useState('BTC');
  const [timeframe, setTimeframe] = useState('1D');
  const [chartType, setChartType] = useState('line');

  const cryptoOptions = [
    { symbol: 'BTC', name: 'Bitcoin', color: '#F7931A' },
    { symbol: 'ETH', name: 'Ethereum', color: '#627EEA' },
    { symbol: 'ADA', name: 'Cardano', color: '#0033AD' }
  ];

  const timeframes = ['1H', '4H', '1D', '1W', '1M'];

  // Mock chart data
  const generateChartData = (crypto, timeframe) => {
    const basePrice = crypto === 'BTC' ? 45000 : crypto === 'ETH' ? 2800 : 0.48;
    const dataPoints = timeframe === '1H' ? 24 : timeframe === '4H' ? 24 : timeframe === '1D' ? 24 : timeframe === '1W' ? 7 : 30;
    
    return Array.from({ length: dataPoints }, (_, i) => {
      const variance = (Math.random() - 0.5) * 0.1;
      const price = basePrice * (1 + variance + (i * 0.002));
      const volume = Math.random() * 1000000000;
      
      return {
        time: timeframe === '1H' ? `${i}:00` : 
              timeframe === '4H' ? `${i * 4}:00` :
              timeframe === '1D' ? `${i}:00` :
              timeframe === '1W' ? `Day ${i + 1}` :
              `${i + 1}`,
        price: parseFloat(price?.toFixed(crypto === 'ADA' ? 4 : 2)),
        volume: Math.floor(volume),
        high: parseFloat((price * 1.02)?.toFixed(crypto === 'ADA' ? 4 : 2)),
        low: parseFloat((price * 0.98)?.toFixed(crypto === 'ADA' ? 4 : 2))
      };
    });
  };

  const chartData = generateChartData(selectedCrypto, timeframe);
  const currentCrypto = cryptoOptions?.find(c => c?.symbol === selectedCrypto);
  const currentPrice = chartData?.[chartData?.length - 1]?.price || 0;
  const previousPrice = chartData?.[chartData?.length - 2]?.price || 0;
  const priceChange = currentPrice - previousPrice;
  const priceChangePercent = ((priceChange / previousPrice) * 100)?.toFixed(2);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-elevation-2">
          <p className="text-sm font-medium text-popover-foreground mb-2">{label}</p>
          <div className="space-y-1">
            <div className="flex justify-between space-x-4">
              <span className="text-xs text-muted-foreground">Price:</span>
              <span className="text-xs font-medium text-popover-foreground">
                ${data?.price?.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between space-x-4">
              <span className="text-xs text-muted-foreground">High:</span>
              <span className="text-xs font-medium text-success">
                ${data?.high?.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between space-x-4">
              <span className="text-xs text-muted-foreground">Low:</span>
              <span className="text-xs font-medium text-error">
                ${data?.low?.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between space-x-4">
              <span className="text-xs text-muted-foreground">Volume:</span>
              <span className="text-xs font-medium text-popover-foreground">
                ${(data?.volume / 1000000)?.toFixed(1)}M
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const TechnicalIndicators = () => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
      <div className="p-3 bg-muted/30 rounded-lg">
        <div className="flex items-center space-x-2 mb-1">
          <Icon name="TrendingUp" size={14} className="text-success" />
          <span className="text-xs font-medium text-card-foreground">RSI</span>
        </div>
        <p className="text-sm font-bold text-card-foreground">67.8</p>
        <p className="text-xs text-muted-foreground">Neutral</p>
      </div>
      <div className="p-3 bg-muted/30 rounded-lg">
        <div className="flex items-center space-x-2 mb-1">
          <Icon name="Activity" size={14} className="text-primary" />
          <span className="text-xs font-medium text-card-foreground">MACD</span>
        </div>
        <p className="text-sm font-bold text-success">+125.4</p>
        <p className="text-xs text-muted-foreground">Bullish</p>
      </div>
      <div className="p-3 bg-muted/30 rounded-lg">
        <div className="flex items-center space-x-2 mb-1">
          <Icon name="BarChart3" size={14} className="text-warning" />
          <span className="text-xs font-medium text-card-foreground">Volume</span>
        </div>
        <p className="text-sm font-bold text-card-foreground">1.2B</p>
        <p className="text-xs text-muted-foreground">Above Avg</p>
      </div>
      <div className="p-3 bg-muted/30 rounded-lg">
        <div className="flex items-center space-x-2 mb-1">
          <Icon name="Target" size={14} className="text-accent" />
          <span className="text-xs font-medium text-card-foreground">Support</span>
        </div>
        <p className="text-sm font-bold text-card-foreground">$44,200</p>
        <p className="text-xs text-muted-foreground">Strong</p>
      </div>
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="LineChart" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-card-foreground">Technical Analysis</h2>
              <p className="text-sm text-muted-foreground">Interactive price charts with indicators</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => window.location.href = '/market-analysis'}>
            Advanced Charts
          </Button>
        </div>

        {/* Crypto Selector */}
        <div className="flex items-center space-x-4 mb-4">
          <div className="flex space-x-2">
            {cryptoOptions?.map((crypto) => (
              <button
                key={crypto?.symbol}
                onClick={() => setSelectedCrypto(crypto?.symbol)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-standard ${
                  selectedCrypto === crypto?.symbol
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                }`}
              >
                {crypto?.symbol}
              </button>
            ))}
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-lg font-bold text-card-foreground">
              ${currentPrice?.toLocaleString()}
            </span>
            <div className="flex items-center space-x-1">
              <Icon 
                name={priceChange > 0 ? 'ArrowUp' : 'ArrowDown'} 
                size={14} 
                className={priceChange > 0 ? 'text-success' : 'text-error'} 
              />
              <span className={`text-sm font-medium ${priceChange > 0 ? 'text-success' : 'text-error'}`}>
                {priceChangePercent}%
              </span>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between">
          <div className="flex space-x-1 bg-muted/30 rounded-lg p-1">
            {timeframes?.map((tf) => (
              <button
                key={tf}
                onClick={() => setTimeframe(tf)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-standard ${
                  timeframe === tf
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {tf}
              </button>
            ))}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setChartType('line')}
              className={`p-2 rounded-md transition-standard ${
                chartType === 'line' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="TrendingUp" size={16} />
            </button>
            <button
              onClick={() => setChartType('area')}
              className={`p-2 rounded-md transition-standard ${
                chartType === 'area' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="AreaChart" size={16} />
            </button>
          </div>
        </div>
      </div>
      <div className="p-6">
        <TechnicalIndicators />
        
        <div className="h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'line' ? (
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="time" 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                  domain={['dataMin - 100', 'dataMax + 100']}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="price" 
                  stroke={currentCrypto?.color || 'var(--color-primary)'}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, fill: currentCrypto?.color || 'var(--color-primary)' }}
                />
              </LineChart>
            ) : (
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="time" 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                  domain={['dataMin - 100', 'dataMax + 100']}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area 
                  type="monotone" 
                  dataKey="price" 
                  stroke={currentCrypto?.color || 'var(--color-primary)'}
                  fill={`${currentCrypto?.color || 'var(--color-primary)'}20`}
                  strokeWidth={2}
                />
              </AreaChart>
            )}
          </ResponsiveContainer>
        </div>
      </div>
      <div className="p-4 border-t border-border bg-muted/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Icon name="Activity" size={16} className="text-success" />
              <span className="text-sm text-muted-foreground">Market is active</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Clock" size={16} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Real-time data</span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="ghost" size="sm">
              <Icon name="Settings" size={16} />
              Indicators
            </Button>
            <Button variant="ghost" size="sm">
              <Icon name="Maximize2" size={16} />
              Fullscreen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveChart;